import { useMemo, useState, useEffect } from "react";
import { Trash2, Filter, Pencil, Flag, RefreshCw } from "lucide-react";
import { formatDate } from "../../lib/api";

// Live "Parsing… ~Ns left" hint with a progress bar, driven by the server's
// processing_started_at + est_seconds. Ticks every second while a row is parsing.
function ProcessingHint({ statement }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const startMs = statement.processing_started_at
    ? new Date(statement.processing_started_at).getTime()
    : now;
  const est = Math.max(4, statement.est_seconds || 15);
  const elapsed = Math.max(0, (now - startMs) / 1000);
  const pct = Math.min(96, Math.round((elapsed / est) * 100));
  const remain = Math.max(0, Math.round(est - elapsed));
  return (
    <span className="inline-flex flex-col gap-1 min-w-[130px] align-middle" data-testid={`statement-processing-${statement.id}`}>
      <span className="inline-flex items-center gap-1 text-[11px] text-[#C84B31] font-medium">
        <RefreshCw className="w-3 h-3 animate-spin" />
        {remain > 0 ? `Parsing… ~${remain}s left` : "Parsing… almost done"}
      </span>
      <span className="h-1 w-full bg-[#EFE3DE] rounded-full overflow-hidden">
        <span
          className="block h-full bg-[#C84B31] rounded-full transition-all duration-1000 ease-linear"
          style={{ width: `${Math.max(6, pct)}%` }}
          data-testid={`statement-progress-bar-${statement.id}`}
        />
      </span>
    </span>
  );
}

function monthsCovered(s) {
  const start = s.period_start || s.uploaded_at;
  const end = s.period_end || s.period_start || s.uploaded_at;
  if (!start) return [];
  const d = new Date(start);
  const e = new Date(end || start);
  if (isNaN(d.getTime())) return [];
  d.setDate(1);
  const out = [];
  let guard = 0;
  const last = isNaN(e.getTime()) ? d : e;
  while (d <= last && guard < 60) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    d.setMonth(d.getMonth() + 1);
    guard++;
  }
  return out;
}

function monthLabel(ym) {
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-IN", { month: "short", year: "numeric" });
}

export function UploadedStatements({ statements, removeStatement, onEdit, onFlag, onReparse }) {
  const [fBank, setFBank] = useState("all");
  const [fMonth, setFMonth] = useState("all");

  const banks = useMemo(
    () => [...new Set(statements.map((s) => s.account_label || s.bank_name).filter(Boolean))].sort(),
    [statements]
  );
  const months = useMemo(() => {
    const set = new Set();
    statements.forEach((s) => monthsCovered(s).forEach((m) => set.add(m)));
    return [...set].sort().reverse();
  }, [statements]);

  const filtered = useMemo(
    () =>
      statements.filter(
        (s) =>
          (fBank === "all" || (s.account_label || s.bank_name) === fBank) &&
          (fMonth === "all" || monthsCovered(s).includes(fMonth))
      ),
    [statements, fBank, fMonth]
  );

  const hasFilter = fBank !== "all" || fMonth !== "all";
  const selectCls = "text-sm border border-[#D8D6CE] rounded px-2 py-1 bg-white focus:outline-none focus:border-[#C84B31]";

  return (
    <div className="bg-white border border-[#E5E3DB] p-6 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="section-title mb-0">Uploaded Statements</div>
        {statements.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="statement-filters">
            <span className="inline-flex items-center gap-1 text-xs text-[#A09E96]"><Filter className="w-3.5 h-3.5" /></span>
            <select value={fBank} onChange={(e) => setFBank(e.target.value)} className={selectCls} data-testid="statement-filter-bank">
              <option value="all">All accounts</option>
              {banks.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <select value={fMonth} onChange={(e) => setFMonth(e.target.value)} className={selectCls} data-testid="statement-filter-month">
              <option value="all">All months</option>
              {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
            {hasFilter && (
              <button
                onClick={() => { setFBank("all"); setFMonth("all"); }}
                className="text-xs text-[#C84B31] hover:opacity-70 font-medium"
                data-testid="statement-filter-clear"
              >
                Clear
              </button>
            )}
            <span className="text-xs text-[#68665E]" data-testid="statement-filter-count">{filtered.length} of {statements.length}</span>
          </div>
        )}
      </div>

      {statements.length === 0 ? (
        <p className="text-sm text-[#68665E]">No statements uploaded yet.</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-[#68665E]" data-testid="statement-no-match">No statements match this filter.</p>
      ) : (
        <div className="table-wrap">
          <table className="w-full text-sm">
            <thead><tr>
              <th className="table-th">Bank / Account</th><th className="table-th">File</th><th className="table-th">Period</th>
              <th className="table-th text-right">Txns</th><th className="table-th">Uploaded</th><th className="table-th text-right">Edit</th>
            </tr></thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.id} className="table-row" data-testid={`statement-row-${s.id}`}>
                  <td className="table-td font-medium">
                    <div className="flex items-center gap-2">
                      <span data-testid={`statement-account-${s.id}`}>{s.account_label || s.bank_name || s.filename}</span>
                      {s.status === "processing" && <ProcessingHint statement={s} />}
                      {s.status === "failed" && (
                        <span className="badge-error text-[11px]" title={s.error || "Could not parse this statement"} data-testid={`statement-failed-${s.id}`}>
                          failed
                        </span>
                      )}
                      {s.status !== "processing" && s.status !== "failed" && s.bank_name === "Unknown Bank" && (
                        <button onClick={() => onEdit?.(s)} className="badge-error text-[11px] hover:opacity-80" data-testid={`statement-set-bank-${s.id}`}>
                          Set bank
                        </button>
                      )}
                      {s.tamper_issues?.length > 0 && <span className="badge-error text-[11px]">tamper</span>}
                      {s.parse_flagged && (
                        <span className="badge-error text-[11px]" title={s.parse_flag_reason || "Marked as parsed incorrectly"} data-testid={`statement-flagged-${s.id}`}>
                          parse issue
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="table-td text-[#68665E] max-w-[200px] truncate" title={s.filename}>{s.filename}</td>
                  <td className="table-td whitespace-nowrap text-xs">{formatDate(s.period_start) || "?"} → {formatDate(s.period_end) || "?"}</td>
                  <td className="table-td text-right">{s.status === "processing" ? "…" : s.txn_count}</td>
                  <td className="table-td text-xs text-[#68665E] whitespace-nowrap">{formatDate(s.uploaded_at)} · {s.uploaded_by_name}</td>
                  <td className="table-td text-right">
                    <div className="inline-flex items-center gap-3">
                      <button
                        onClick={() => {
                          if (window.confirm("Re-parse this statement from the original file? Its current transactions will be replaced.")) onReparse?.(s);
                        }}
                        className={s.parse_flagged ? "text-[#C84B31]" : "text-[#68665E] hover:text-[#C84B31]"}
                        title="Re-parse from the original file"
                        data-testid={`reparse-statement-${s.id}`}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (s.parse_flagged) { onFlag?.(s, false); return; }
                          const reason = window.prompt("What looks wrong with this statement's parsing? (optional)", "");
                          if (reason !== null) onFlag?.(s, true, reason);
                        }}
                        className={s.parse_flagged ? "text-[#C84B31]" : "text-[#68665E] hover:text-[#C84B31]"}
                        title={s.parse_flagged ? "Clear parse-issue flag" : "Report incorrect parsing"}
                        data-testid={`flag-statement-${s.id}`}
                      >
                        <Flag className="w-4 h-4" />
                      </button>
                      <button onClick={() => onEdit?.(s)} className="text-[#68665E] hover:text-[#C84B31]" title="Edit bank & account" data-testid={`edit-statement-${s.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeStatement(s)} className="text-[#8A2526] hover:opacity-70" data-testid={`delete-statement-${s.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
